// @givmo/api-client — client wiring.
//
// The generated code ships a shared singleton `client` (src/generated/client.gen.ts)
// that the generated SDK functions and TanStack Query options bind to by default.
// An app calls `configureGivmoClient(config)` ONCE at startup to set the base URL
// and install the Givmo interceptor stack onto that singleton; every generated
// `sdk.*` call and `*Options` hook then inherits the configuration.
//
// `createGivmoClient(config)` builds an ISOLATED client (its own interceptors +
// base URL) for tests and any scenario needing more than one configuration in a
// process (e.g. SSR per-request clients). Pass it as `{ client }` on an SDK call.

import { createClient } from './generated/client';
import { client as sharedClient } from './generated/client.gen';
import type { Client } from './generated/client';
import { resolveBaseUrl, type GivmoClientConfig } from './config';
import { installMiddleware, type InstalledMiddleware } from './middleware';

export interface ConfiguredGivmoClient {
  /** The configured fetch client (the shared singleton, unless created isolated). */
  readonly client: Client;
  readonly installed: InstalledMiddleware;
}

function configure(client: Client, config: GivmoClientConfig): ConfiguredGivmoClient {
  client.setConfig({ baseUrl: resolveBaseUrl(config.baseUrl) });
  const installed = installMiddleware(client, config);
  return { client, installed };
}

/**
 * Configure the shared generated client used by `sdk.*` and the TanStack Query
 * option factories. Call once during app bootstrap. Returns the configured
 * client + what the middleware stamped (base URL, prefix).
 */
export function configureGivmoClient(config: GivmoClientConfig): ConfiguredGivmoClient {
  return configure(sharedClient, config);
}

/**
 * Build an isolated client with its own interceptor stack + base URL. Use for
 * tests and per-request SSR clients. Pass the returned `client` as `{ client }`
 * on an SDK call to route it through this configuration instead of the shared one.
 */
export function createGivmoClient(config: GivmoClientConfig): ConfiguredGivmoClient {
  const client = createClient();
  return configure(client, config);
}

/** The shared generated client (already imported by the generated SDK + hooks). */
export { sharedClient as givmoClient };
